import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import PaginationControl from "@/components/common/PaginationControl";
import AgentsManagement from "@/components/teams/AgentsManagement";
import TeamForm from "@/components/teams/TeamForm";
import TeamsList from "@/components/teams/TeamsList";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { teamService } from "@/services/team.service/team.api";
import useStore from "@/store/useStore";
import { AgentRole, type Agent, type Team } from "@/types/team";
import { PlusCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export default function Teams() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [isEditTeamDialogOpen, setIsEditTeamDialogOpen] = useState(false);
  const [isAgentsManagementDialogOpen, setIsAgentsManagementDialogOpen] =
    useState(false);
  const [isAddAgentDialogOpen, setIsAddAgentDialogOpen] = useState(false);
  const [isEditAgentDialogOpen, setIsEditAgentDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("teams");
  const [currentTeam, setCurrentTeam] = useState<Team>({
    _id: "",
    name: "",
    memberCount: 0,
    isActive: true,
  });
  const [currentAgent, setCurrentAgent] = useState<Partial<Agent>>({
    permissions: [AgentRole.APP_AGENT],
  });
  // Lista de usuários disponíveis

  // Estados de loading para botões
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isAddingAgent, setIsAddingAgent] = useState(false);
  const [isEditingAgent, setIsEditingAgent] = useState(false);

  // Paginação
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Loading state usando o estado global
  const loading = useStore((state) => state.commonSlice.loaders.getAllTeams);


  // Buscar equipes
  const fetchTeams = useCallback(async () => {
    const searchParams = query.trim()
      ? { search: { s_name: query.trim() } }
      : {};

    const [res, error] = await teamService.getAll(searchParams, page, limit);

    if (error) {
      console.error("Erro ao buscar equipes:", error);
      toast.error("Erro ao buscar equipes");
      return;
    }

    if (res) {
      console.log("Equipes recebidas da API:", res);

      // Verificar e mapear o formato correto dos dados
      if (Array.isArray(res.result)) {
        // Mapear os dados para garantir que estão no formato correto
        const mappedTeams = res.result.map((team) => {

          // Construir um objeto Team válido
          return {
            _id: team._id,
            name: team.name || "",
            description: team.description,
            isActive: team.isActive !== undefined ? team.isActive : true,
            memberCount: team.memberCount,
          };
        });
        setTeams(mappedTeams);
      } else {
        setTeams([]);
      }

      setTotalPages(res.qtdPages);
      setTotalItems(res.total);
    }
  }, [query, page, limit]);

  // Criar equipe
  const handleCreateTeam = async () => {
    if (!currentTeam.name) {
      toast.error("O nome da equipe é obrigatório");
      return;
    }

    setIsCreatingTeam(true);

    const [res, error] = await teamService.create({
      name: currentTeam.name,
      description: currentTeam.description,
      isActive: currentTeam.isActive,
    });

    setIsCreatingTeam(false);

    if (error) {
      return;
    }

    if (res) {
      console.log("Equipe criada com sucesso:", res);
      fetchTeams();
      setCurrentTeam({
        _id: "",
        name: "",
        memberCount: 0,
        isActive: true,
      });
      setIsCreateTeamDialogOpen(false);
      toast.success("Equipe criada com sucesso!");
    }
  };

  // Editar equipe
  const handleEditTeam = async () => {
    if (!currentTeam.name) {
      toast.error("O nome da equipe é obrigatório");
      return;
    }

    setIsEditingTeam(true);

    const [res, error] = await teamService.update(currentTeam._id, {
      name: currentTeam.name,
      description: currentTeam.description,
      isActive: currentTeam.isActive,
    });

    setIsEditingTeam(false);

    if (error) {
      return;
    }

    if (res) {
      fetchTeams();
      setIsEditTeamDialogOpen(false);
      toast.success("Equipe atualizada com sucesso!");
    }
  };



  // Paginação
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  };

  // Carregar equipes no carregamento da página
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);




  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Equipes</h1>
        <Drawer
          open={isCreateTeamDialogOpen}
          onOpenChange={setIsCreateTeamDialogOpen}
        >
          <DrawerTrigger asChild>
            <Button className="gap-2">
              <PlusCircle className="h-4 w-4" />
              Nova Equipe
            </Button>
          </DrawerTrigger>
          <DrawerContent className="sm:max-w-2xl">
            <div className="px-6 py-4">
              <DrawerHeader>
                <DrawerTitle>Criar Equipe</DrawerTitle>
                <DrawerDescription>
                  Defina um nome para a equipe e uma descrição
                </DrawerDescription>
              </DrawerHeader>
              <TeamForm
                team={currentTeam}
                setTeam={setCurrentTeam}
                onSave={handleCreateTeam}
                onCancel={() => setIsCreateTeamDialogOpen(false)}
                isLoading={isCreatingTeam}
              />
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      <div className="grid gap-6">
        <TeamsList
          teams={teams}
          onEdit={(team) => {
            setCurrentTeam(team);
            setIsEditTeamDialogOpen(true);
          }}
          onManageAgents={(team) => {
            setCurrentTeam(team);
            setIsAgentsManagementDialogOpen(true);
          }}
        />

        <LoadingOverlay isLoading={loading} />

        {teams.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center p-12 bg-muted/30 rounded-lg">
            <p className="text-muted-foreground text-center">
              Nenhuma equipe encontrada
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setIsCreateTeamDialogOpen(true)}
            >
              Criar Equipe
            </Button>
          </div>
        )}

        <PaginationControl
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Dialogs de edição e gerenciamento de agentes */}
      <Drawer
        open={isEditTeamDialogOpen}
        onOpenChange={setIsEditTeamDialogOpen}
      >
        <DrawerContent className="sm:max-w-2xl">
          <div className="px-6 py-4">
            <DrawerHeader>
              <DrawerTitle>Editar Equipe</DrawerTitle>
              <DrawerDescription>
                Atualize as informações da equipe
              </DrawerDescription>
            </DrawerHeader>
            <TeamForm
              team={currentTeam}
              setTeam={setCurrentTeam}
              onSave={handleEditTeam}
              onCancel={() => setIsEditTeamDialogOpen(false)}
              isLoading={isEditingTeam}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={isAgentsManagementDialogOpen}
        onOpenChange={setIsAgentsManagementDialogOpen}
      >
        <DrawerContent className="sm:max-w-xl">
          <div className="px-6 py-4">

            <AgentsManagement
              teamId={currentTeam._id}
              onClose={() => setIsAgentsManagementDialogOpen(false)}
              onRefresh={fetchTeams}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={isEditAgentDialogOpen}
        onOpenChange={setIsEditAgentDialogOpen}
      >
        <DrawerContent className="sm:max-w-2xl">
          <div className="px-6 py-4">
            <DrawerHeader>
              <DrawerTitle>Editar Função do Agente</DrawerTitle>
              <DrawerDescription>
                Altere a função do agente na equipe
              </DrawerDescription>
            </DrawerHeader>
            {/* <AgentForm
                agent={currentAgent}
                setAgent={setCurrentAgent}

                isEditMode={true}
                onSave={handleEditAgent}
                onCancel={() => setIsEditAgentDialogOpen(false)}
                isLoading={isEditingAgent}
              /> */}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
